import { useEffect, useRef, useState } from 'react'
import type { Game, Room } from '../../types/game'
import { sendChatMessage } from '../../api/rooms'
import { SeasonIcon } from '../shared/ResourceIcon'
import { PlayerMarker } from '../shared/PlayerMarker'
import { formatGameMessage } from '../../utils/formatMessage'
import { buildMobileLogEntries } from './mobileLogEntries'
import type { Color } from '../../types/game'

interface MobileChatModalProps {
  room: Room
  game: Game
  memberId: string
  activePanel: string | null
  onClose: () => void
}

export function MobileChatModal({
  room,
  game,
  memberId,
  activePanel,
  onClose,
}: MobileChatModalProps) {
  const [chatText, setChatText] = useState('')
  const messagesRef = useRef<HTMLDivElement>(null)
  const entries = buildMobileLogEntries(room, game, memberId).slice(-60)

  useEffect(() => {
    if (activePanel === 'chat' && messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight
    }
  }, [activePanel, entries])

  const handleSend = async () => {
    const text = chatText.trim()
    if (!text) return
    try {
      await sendChatMessage(room.id, { member_id: memberId, text })
      setChatText('')
    } catch {
      // silent
    }
  }

  return (
    <div id="mobile-chat-modal" className={`mobile-panel modal-panel mobile-chat-modal${activePanel === 'chat' ? ' open' : ''}`}>
      <div className="mobile-panel-shell">
        <div className="mobile-panel-header">
          <div className="mobile-panel-title-main">Chat</div>
          <div id="mobile-close-chat" className="mobile-panel-close lobby-start" onClick={onClose}>Close</div>
        </div>

        <div id="mobile-chat-messages" className="mobile-chat-messages messages" ref={messagesRef}>
          {entries.map((entry, i) =>
            entry.kind === 'game' ? (
              <div key={`game-${i}`} className="message">
                <SeasonIcon season={entry.season!} inverted />
                <div>
                  {entry.rawText && entry.aliasByColor
                    ? formatGameMessage(entry.rawText, entry.aliasByColor)
                    : entry.text}
                </div>
              </div>
            ) : (
              <div key={`chat-${i}`} className="message">
                {entry.color && <PlayerMarker color={entry.color as Color} className="opponent-marker" />}
                <div>
                  <span style={{ color: 'var(--grey)' }}>{entry.sender}:</span>
                  {' '}{entry.text}
                </div>
              </div>
            )
          )}
        </div>

        <div className="mobile-chat-composer">
          <input
            id="mobile-chat-input"
            className="mobile-chat-input input"
            placeholder="Type a message..."
            value={chatText}
            onChange={(e) => setChatText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void handleSend()
              }
            }}
          />
          <div id="mobile-chat-send" className="mobile-chat-send lobby-start" onClick={() => void handleSend()}>
            Send
          </div>
        </div>
      </div>
    </div>
  )
}
